import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { EASE, Kicker, Reveal } from "@/components/Reveal";
import BookingCalendar from "@/components/BookingCalendar";
import { BOOKING_SERVICES, BUSINESS } from "@/data/site";
import { createBooking } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";

const STEPS = ["Service", "Date & Time", "Your Details", "Deposit"];

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-0" data-testid="booking-steps">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className="flex flex-col items-center">
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-full border font-mono text-xs transition-colors duration-300 ${
                i < step
                  ? "border-[#B89759] bg-[#B89759] text-[#FAF7F2]"
                  : i === step
                    ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]"
                    : "border-[#D4AF37]/40 text-[#8C7E70]"
              }`}
              data-testid={`booking-step-indicator-${i}`}
            >
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <span className="mt-2 hidden font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C7E70] sm:block">
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && <span className="mx-2 h-px w-8 bg-[#D4AF37]/40 sm:mx-4 sm:w-14" />}
        </div>
      ))}
    </div>
  );
}

export default function Booking() {
  usePageMeta(
    "Book a Consultation | Diamond's Divine Arts",
    "Book a one-on-one consultation, commission inquiry, or artwork discussion with Natalia of Diamond's Divine Arts."
  );

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    service: "",
    date: "",
    time: "",
    name: "",
    email: "",
    phone: "",
    message: "",
    deposit: "later",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const service = BOOKING_SERVICES.find((s) => s.id === form.service);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const today = new Date().toISOString().split("T")[0];

  const validate = () => {
    if (step === 0 && !form.service) return "Please choose a service to continue.";
    if (step === 1) {
      if (!form.date) return "Please pick a preferred date.";
      if (!form.time) return "Please pick a preferred time.";
    }
    if (step === 2) {
      if (form.name.trim().length < 2) return "Please enter your name.";
      if (!/\S+@\S+\.\S+/.test(form.email)) return "Please enter a valid email address.";
      if (form.phone.trim().length < 7) return "Please enter your phone number.";
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await createBooking({
        service: service.name,
        date: form.date,
        time: form.time,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        deposit: form.deposit,
      });
      setResult(res);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error("Something went wrong sending your request. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <section className="divine-rays relative overflow-hidden py-20 md:py-32" data-testid="booking-success">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#F4EFE6]"
          >
            <Check className="h-9 w-9 text-[#9A7A3F]" />
          </motion.div>
          <Reveal delay={0.15}>
            <h1 className="mt-8 font-serif text-4xl font-light tracking-tight text-[#1C1815] sm:text-5xl">
              Booking request received.
            </h1>
            <p className="mt-4 font-mono text-sm uppercase tracking-[0.3em] text-[#9A7A3F]" data-testid="booking-reference">
              Reference · {result.reference}
            </p>
            <p className="mx-auto mt-6 max-w-md text-base font-light leading-relaxed text-[#4A423A]">
              Thank you, {result.name.split(" ")[0]}. Your request for a{" "}
              <span className="text-[#1C1815]">{result.service}</span> on {result.date} at{" "}
              {result.time} has been saved. Natalia will confirm your appointment by email.
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-[#8C7E70]">
              A copy of your request has been saved — Natalia will be in touch shortly.
            </p>
            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/" className="btn-outline" data-testid="booking-success-home">
                Return Home
              </Link>
              <Link to="/portfolio" className="btn-primary" data-testid="booking-success-portfolio">
                Explore the Art <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section className="divine-rays relative overflow-hidden py-16 md:py-24" data-testid="booking-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <Reveal className="text-center">
          <Kicker className="justify-center">Booking & Consultations</Kicker>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#1C1815] sm:text-5xl">
            Book your <span className="italic text-[#9A7A3F]">one-on-one</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base font-light leading-relaxed text-[#4A423A]">
            A simple, four-step request. Natalia personally confirms every appointment.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-12 flex justify-center">
          <StepIndicator step={step} />
        </Reveal>

        <div className="mt-12 rounded-sm border border-[#D4AF37]/30 bg-[#FAF7F2]/80 p-6 shadow-sm sm:p-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              {step === 0 && (
                <div data-testid="booking-step-service">
                  <h2 className="font-serif text-2xl text-[#1C1815]">What would you like to discuss?</h2>
                  <div className="mt-7 grid gap-4 sm:grid-cols-2">
                    {BOOKING_SERVICES.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => set("service", s.id)}
                        className={`rounded-sm border p-5 text-left transition-colors duration-200 ${
                          form.service === s.id
                            ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]"
                            : "border-[#D4AF37]/40 bg-white/50 hover:border-[#B89759]"
                        }`}
                        data-testid={`booking-service-${s.id}`}
                      >
                        <span className="block font-serif text-lg">{s.name}</span>
                        <span
                          className={`mt-1 block text-xs font-light ${
                            form.service === s.id ? "text-[#D9CDB8]" : "text-[#8C7E70]"
                          }`}
                        >
                          {s.detail}
                        </span>
                        <span
                          className={`mt-3 inline-block rounded-full px-3 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${
                            form.service === s.id
                              ? "bg-[#D4AF37]/20 text-[#E7D3A2]"
                              : "bg-[#D4AF37]/15 text-[#9A7A3F]"
                          }`}
                        >
                          {s.deposit}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="booking-step-datetime">
                  <h2 className="font-serif text-2xl text-[#1C1815]">Pick a date & time</h2>
                  <BookingCalendar
                    date={form.date}
                    time={form.time}
                    onDate={(d) => set("date", d)}
                    onTime={(t) => set("time", t)}
                  />
                </div>
              )}

              {step === 2 && (
                <div data-testid="booking-step-details">
                  <h2 className="font-serif text-2xl text-[#1C1815]">Your details</h2>
                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="kicker">Name</span>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Your full name"
                        className="field mt-2.5"
                        data-testid="booking-name-input"
                      />
                    </label>
                    <label className="block">
                      <span className="kicker">Email</span>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="you@example.com"
                        className="field mt-2.5"
                        data-testid="booking-email-input"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="kicker">Phone</span>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="(000) 000-0000"
                        className="field mt-2.5"
                        data-testid="booking-phone-input"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="kicker">Project Vision / Message (optional)</span>
                      <textarea
                        rows={4}
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                        placeholder="Tell Natalia about your idea, your space, or a scripture that inspires you…"
                        className="field mt-2.5 resize-none"
                        data-testid="booking-message-input"
                      />
                    </label>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="booking-step-deposit">
                  <h2 className="font-serif text-2xl text-[#1C1815]">Review & deposit</h2>

                  <dl className="mt-7 space-y-3 rounded-sm border border-[#D4AF37]/30 bg-[#F4EFE6]/70 p-6 text-sm">
                    <div className="flex justify-between gap-4">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">Service</dt>
                      <dd className="text-right text-[#1C1815]">{service?.name}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">When</dt>
                      <dd className="text-right text-[#1C1815]">
                        {form.date} · {form.time}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">For</dt>
                      <dd className="text-right text-[#1C1815]">{form.name}</dd>
                    </div>
                  </dl>

                  {service?.depositAmount ? (
                    <p className="mt-7 flex items-start gap-2 text-sm font-light leading-relaxed text-[#4A423A]">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#B89759]" />
                      Reserve your slot now — Natalia will confirm your appointment and share the {service.depositAmount} deposit details by email.
                    </p>
                  ) : (
                    <p className="mt-7 flex items-start gap-2 text-sm font-light leading-relaxed text-[#4A423A]">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#B89759]" />
                      No deposit needed for this request — Natalia will reply personally to arrange
                      next steps.
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-between border-t border-[#D4AF37]/25 pt-7">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(s - 1, 0))}
              className={`btn-ghost-light !border-[#1C1815]/20 !text-[#4A423a] hover:!border-[#B89759] ${
                step === 0 ? "pointer-events-none opacity-0" : ""
              }`}
              data-testid="booking-back-button"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} className="btn-primary" data-testid="booking-next-button">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="btn-primary disabled:opacity-60"
                data-testid="booking-submit-button"
              >
                {submitting ? "Sending…" : "Confirm Booking Request"} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <Reveal className="mt-8 text-center">
          <p className="text-sm font-light text-[#8C7E70]">
            Prefer to talk? Call{" "}
            <a href={BUSINESS.phoneHref} className="text-[#9A7A3F] underline-offset-4 hover:underline" data-testid="booking-call-link">
              {BUSINESS.phone}
            </a>{" "}
            or email{" "}
            <a href={`mailto:${BUSINESS.email}`} className="text-[#9A7A3F] underline-offset-4 hover:underline" data-testid="booking-email-link">
              {BUSINESS.email}
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
